import { Injectable } from "@angular/core";
import { ActivatedRoute, ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from "@angular/router";
import { RegionEnum } from "@edp-enums/region.enum";
import { environment } from "@edp-environments/environment";
import { IDevice } from "@edp-models/device.model";
import { EdpResponseBody } from "@edp-models/response.model";
import { EdpUser, EdpUserFactory } from "@edp-models/user.model";
import { AppService } from "@edp-services/app.service";
import { AuthenticationService } from "@edp-services/authentication.service";
import { DeepLinkService } from "@edp-services/deep-link.service";
import { EnvService } from "@edp-services/env.service";
import { NotificationsService } from "@edp-services/notifications.service";
import { PushNotificationService } from "@edp-services/push-notifications.service";
import { RegionService } from "@edp-services/region.service";
import { GetSolarAccountEs } from "@edp-store/actions/ES/solar-account-es.actions";
import { ShowNewLocalToastr } from "@edp-store/actions/dashboard.actions";
import { ShowLoader } from "@edp-store/actions/layout.actions";
import { RegisterDevice } from "@edp-store/actions/notifications.actions";
import { IsUserEdpE, SetUser } from "@edp-store/actions/user.actions";
import { getUser } from "@edp-store/selectors/user.selectors";
import { IAppEsState } from "@edp-store/state/ES/app-es.state";
import { IAppState } from "@edp-store/state/app.state";
import { AuthService } from "@edpc/edp-auth";
import { Store } from "@ngrx/store";
import { User } from "firebase";
import { CookieService } from "ngx-cookie-service";
import { DeviceDetectorService } from "ngx-device-detector";
import { Observable, of, throwError } from "rxjs";
import { catchError, concatMap, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

const keyAnalyticsLocalStorage = "analytics_queryparams";
const keyRouteRedirectLocalStorage = "redirectToRoute";

@Injectable({
    providedIn: "root",
})
export class EdpAuthGuard implements CanActivate {
    private isImpersonate: boolean;

    constructor(
        private auth: AuthService,
        private authenticationService: AuthenticationService,
        private cookieService: CookieService,
        private store: Store<IAppState>,
        private storeEs: Store<IAppEsState>,
        private router: Router,
        private appService: AppService,
        private pushNotification: PushNotificationService,
        private deepLinkService: DeepLinkService,
        private activatedRoute: ActivatedRoute,
        private notificationService: NotificationsService,
        private deviceDetector: DeviceDetectorService,
        private envService: EnvService,
        private regionService: RegionService
    ) {}

    canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> | Promise<any> | boolean {
        return this.checkImpersonate(next).pipe(
            switchMap(() => this.auth.getStrategyToken("sso")),
            withLatestFrom(this.store.select(getUser)),
            concatMap(([token, user]) => {
                if (user && user.uid === token.getPayload().user_id) {
                    return of(true);
                } else {
                    const cookieIsImpersonate = this.isImpersonate || !!this.cookieService.get("is_impersonate");
                    return this.authenticationService.validateToken(token.getValue(), cookieIsImpersonate);
                }
            }),
            switchMap(response => {
                if (typeof response !== "boolean") {
                    const result: EdpResponseBody = response as EdpResponseBody;
                    if (!result.Success) {
                        return throwError("Login Failed");
                    }

                    let providerData = [];
                    this.auth
                        .getCurrentUser("sso")
                        .pipe(take(1))
                        .subscribe((userData: User) => (providerData = userData ? userData.providerData : []));

                    const tmpUser = { ...result.Result.Collection.data, ...providerData.pop() };
                    const user = EdpUserFactory.create(tmpUser);

                    this.store.dispatch(new SetUser(user));
                    this.store.dispatch(new IsUserEdpE(user.edpESubscribed));
                    this.store.dispatch(new ShowLoader(false));

                    if (!!user && user.loginNr < 3 && next.routeConfig.path === "energia/inicio") {
                        this.store.dispatch(new ShowNewLocalToastr(true));
                    }

                    if (this.regionService.getRegion() === RegionEnum.ES) {
                        this.storeEs.dispatch(new GetSolarAccountEs());
                    }

                    if (environment.isApp) {
                        const deviceInfo = localStorage.getItem("edp-device-info");
                        if (!deviceInfo) {
                            this.appService.getDeviceId().subscribe((args: IDevice) => {
                                this.store.dispatch(new RegisterDevice({ ...args, FirebaseId: user.uid }));
                            });
                        }

                        this.appService.getPreferences("PUSH_PAYLOAD").subscribe(data => {
                            if (data && data.burl) {
                                this.pushNotification.receivePushAndFormatData(data);
                                this.appService.deletePreferences("PUSH_PAYLOAD");
                            }
                        });
                    } else {
                        this.getPushNotificationToken(user);
                    }

                    const _deeplinkRedirectUrl = localStorage.getItem("redirectTo");

                    if (_deeplinkRedirectUrl) {
                        localStorage.removeItem("redirectTo");

                        const url =
                            _deeplinkRedirectUrl.indexOf("?") !== -1
                                ? _deeplinkRedirectUrl.substring(0, _deeplinkRedirectUrl.indexOf("?"))
                                : _deeplinkRedirectUrl;

                        this.router.navigate([url], {
                            queryParams: this.deepLinkService.getAnalyticsQueryParams(),
                        });
                    }

                    const welcomeRoute = "/planeta-zero/bem-vindo";

                    if (user.partnerNumber && !user.isImpersonate) {
                        if (!user.welcomeViewed) {
                            this.router.navigate([welcomeRoute]);
                        }
                    }
                }

                if (!!this.deepLinkService.hasAnalyticsParamsToSet()) {
                    // const routeToRedirect = localStorage.getItem(keyRouteRedirectLocalStorage);
                    const url = state.url.indexOf("?") !== -1 ? state.url.substring(0, state.url.indexOf("?")) : state.url;
                    this.router.navigate([url], {
                        relativeTo: this.activatedRoute,
                        queryParams: this.deepLinkService.getAnalyticsQueryParams(),
                    });
                }
                return of(true);
            }),
            catchError(error => {
                this.checkAnalyticsParams(state);
                const now = new Date();
                now.setMinutes(now.getMinutes() + 5);
                this.cookieService.set("origin_referer", location.href, now, "/", this.envService.getCookieDomain());
                this.authenticationService.logout().subscribe(() => {
                    const queryParamEmail = this.checkParamEmailRedirectSSO(state) || "";
                    if (next.routeConfig.path !== "**") {
                        if (!!next.data.canRedirect) {
                            localStorage.setItem(
                                keyRouteRedirectLocalStorage,
                                state.url.replace("/", "").substring(0, state.url.indexOf("?") - 1)
                            );
                        }
                        window.location.href = environment.isApp
                            ? `${this.envService.ssoUrl()}/?Mobile=1${queryParamEmail}`
                            : this.envService.ssoUrl();
                    } else {
                        localStorage.setItem("redirectTo", state.url);
                        const refererUrl = `${this.envService.ssoUrl()}/?referer=${location.href}`;
                        window.location.href = environment.isApp ? `${refererUrl}&Mobile=1${queryParamEmail}` : refererUrl;
                    }
                });
                return of(false);
            })
        );
    }

    // PLAT-61468
    private checkParamEmailRedirectSSO(state: RouterStateSnapshot): string {
        const key = "email";
        const hasEmail = Object.keys(state.root.queryParams)?.some(i => i.includes(key));
        return !!hasEmail ? `&email=${state.root.queryParams.email}` : null;
    }

    private setImpersonate(token: string): void {
        const now = new Date();
        now.setTime(now.getTime() + 1 * 3600 * 1000 * 24);
        this.cookieService.set("firebase_token", token, now, "/");
        this.cookieService.set("is_impersonate", "true", now, "/");

        this.isImpersonate = true;
    }

    private checkImpersonate(route: ActivatedRouteSnapshot): Observable<any> {
        return of(route.queryParams).pipe(
            switchMap(params => {
                if (!!(params && (!!params.impersonate || !!params.is_impersonate))) {
                    return this.authenticationService.logout().pipe(
                        tap(() => {
                            const token = !!params.impersonate ? params.impersonate : params.token;
                            this.setImpersonate(token);
                        })
                    );
                }
                return of(null);
            })
        );
    }

    private checkAnalyticsParams(state: RouterStateSnapshot): void {
        const keyAnalytics = "utm";
        const hasAnalytics = Object.keys(state.root.queryParams)?.some(i => i.includes(keyAnalytics));
        if (hasAnalytics) {
            localStorage.setItem(keyAnalyticsLocalStorage, JSON.stringify(state.root.queryParams));
        }
    }

    private getPushNotificationToken(user): void {
        if (!!window.Notification && window.Notification.permission !== "denied") {
            this.notificationService
                .requestPermission()
                .then(token => {
                    const device = this.deviceDetector.getDeviceInfo();
                    const deviceId = localStorage.getItem("edp-push-browser")
                        ? localStorage.getItem("edp-push-browser")
                        : this.notificationService.getNewDeviceId();

                    const pushPayload = {
                        BrowserDevice: device.browser,
                        BrowserVersion: device.browser_version,
                        Device: device.device,
                        DeviceType: device.deviceType || "App",
                        OsType: device.os,
                        OsVersion: device.os_version,
                        UserAgent: device.userAgent,
                        RegistrationId: token,
                        DeviceId: deviceId,
                    } as IDevice;

                    this.store.dispatch(new RegisterDevice({ ...pushPayload, FirebaseId: user.uid }));

                    this.notificationService.receiveMessage();
                })
                .catch(error => console.log(error));
        }
    }

    private getDeviceAndNotificationPermission(user: EdpUser): void {
        if (environment.isApp) {
            const deviceInfo = localStorage.getItem("edp-device-info");
            if (!deviceInfo) {
                this.appService.getDeviceId().subscribe((args: IDevice) => {
                    this.store.dispatch(new RegisterDevice({ ...args, FirebaseId: user.uid }));
                });
            }

            this.appService.getPreferences("PUSH_PAYLOAD").subscribe(data => {
                if (data && data.burl) {
                    this.pushNotification.receivePushAndFormatData(data);
                    this.appService.deletePreferences("PUSH_PAYLOAD");
                }
            });
            return;
        }

        try {
            this.getPushNotificationToken(user);
        } catch (error) {
            console.log(error);
        }
    }
}
